import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, authenticateAnonymously } from '../firebase';
import { Game, Location } from '../types';

interface GameDetailProps {
  game: Game;
  leagueName?: string;
  onBack: () => void;
}

export const GameDetail: React.FC<GameDetailProps> = ({ game, leagueName, onBack }) => {
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      if (!game.locationId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Erst authentifizieren
        const authSuccess = await authenticateAnonymously();
        if (!authSuccess) {
          setError('Authentifizierung fehlgeschlagen');
          setLoading(false);
          return;
        }

        const locationDoc = await getDoc(doc(db, 'locations', game.locationId));

        if (locationDoc.exists()) {
          const data = locationDoc.data();

          // Die Location-Daten sind in einem verschachtelten locationInfo-Objekt
          const locationInfo = data.locationInfo || data;
          setLocation({
            id: locationDoc.id,
            name: locationInfo.name,
            street: locationInfo.street,
            zip: locationInfo.zip,
            city: locationInfo.city,
            glueInfo: locationInfo.glueInfo,
            glueAllowed: locationInfo.glueAllowed
          } as Location);
        } else {
          setError('Spielstätte nicht gefunden');
        }
      } catch (err) {
        console.error('Error fetching location:', err);
        setError('Fehler beim Laden der Spielstätte');
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [game.locationId]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Game Detail View */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Back Button */}
          <div className="px-6 py-4 border-b border-gray-200">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zurück
            </button>
          </div>

          {/* Teams Header with Score or VS */}
          <div className="bg-gradient-to-r from-fortuna-50 to-fortuna-100 px-4 py-4 sm:py-5 border-b border-gray-200">
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <div className="flex-1 text-right">
                <p className={`text-sm sm:text-lg font-bold text-gray-900 ${
                  game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.homeScore > game.awayScore ? 'text-fortuna-700' : ''
                }`}>{game.homeTeam}</p>
              </div>
              <div className="flex-shrink-0">
                {game.finished && game.homeScore !== undefined && game.homeScore !== null && game.awayScore !== undefined && game.awayScore !== null ? (
                  <div className="bg-white rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-sm">
                    <span className="text-base sm:text-xl font-bold text-gray-900">{game.homeScore}:{game.awayScore}</span>
                  </div>
                ) : (
                  <div className="bg-white rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-sm">
                    <span className="text-xs sm:text-base font-semibold text-gray-400">vs</span>
                  </div>
                )}
              </div>
              <div className="flex-1 text-left">
                <p className={`text-sm sm:text-lg font-bold text-gray-900 ${
                  game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.awayScore > game.homeScore ? 'text-fortuna-700' : ''
                }`}>{game.awayTeam}</p>
              </div>
            </div>
          </div>

          {/* Game Information */}
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {/* Date & Time Section */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Spieltermin</h3>
              </div>
              <div className="pl-6">
                <p className="text-sm text-gray-900 font-medium">{formatDate(game.date)}</p>
                <p className="text-sm text-gray-900 font-medium">{game.time} Uhr</p>
              </div>
            </div>

            {/* League */}
            {leagueName && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Liga</h3>
                </div>
                <p className="text-sm text-gray-900 pl-6">{leagueName}</p>
              </div>
            )}

            {/* Spielbericht PDF Link */}
            {game.pdfUrl && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Spielbericht</h3>
                </div>
                <div className="pl-6">
                  <a
                    href={game.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-fortuna-600 hover:text-fortuna-700 font-medium"
                  >
                    Spielbericht anzeigen
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            )}

            {/* Referees */}
            {game.refs && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Schiedsrichter</h3>
                </div>
                <p className="text-sm text-gray-900 pl-6">{game.refs}</p>
              </div>
            )}

            {/* Location Loading */}
            {loading && (
              <div className="pt-3 border-t border-gray-100">
                <div className="text-center py-6">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-fortuna-600"></div>
                  <p className="mt-2 text-xs text-gray-600">Lade Spielstätte...</p>
                </div>
              </div>
            )}

            {/* Location Error */}
            {error && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Spielstätte</h3>
                </div>
                <div className="pl-6 text-red-600 text-xs">{error}</div>
              </div>
            )}

            {/* Location with details */}
            {location && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Spielstätte</h3>
                </div>
                <div className="pl-6 space-y-1.5">
                  <p className="text-sm font-semibold text-gray-900">{location.name}</p>
                  <p className="text-sm text-gray-700">
                    {location.street}<br />
                    {location.zip} {location.city}
                  </p>

                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-700 mb-1.5">Haftmittelverwendung</h4>
                    {location.glueInfo ? (
                      <div
                        className="text-xs text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: location.glueInfo }}
                      />
                    ) : (
                      <p className="text-xs text-gray-600">Keine Informationen vorhanden</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Simple location (no detailed info) */}
            {!loading && !error && !location && game.location && (
              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-1.5">
                  <svg className="w-4 h-4 text-fortuna-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Spielort</h3>
                </div>
                <p className="text-sm text-gray-900 pl-6">{game.location}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
